import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

// Allow the marquee effect on all screen widths
const MAX_SCREEN_WIDTH = Infinity;

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {boolean}
   */
  #isActive = false;

  /**
   * Keep track of viewport width so mobile browser chrome
   * height changes do not reset the marquee.
   */
  #lastWidth = window.innerWidth;

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    this.#lastWidth = window.innerWidth;

    window.addEventListener('resize', this.#handleResize);

    if (this.#shouldRunMarquee) {
      this.#setupMarquee();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#handleResize);
    this.#teardownMarquee();
  }

  /**
   * Check if the marquee effect should run based on screen width.
   */
  get #shouldRunMarquee() {
    return window.innerWidth <= MAX_SCREEN_WIDTH;
  }

  /**
   * Sets up the marquee.
   */
  #setupMarquee() {
    if (this.#isActive) return;

    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    // Desktop hover
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);

    // Mobile / touch hold
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerUp);

    this.#isActive = true;
  }

  /**
   * Tears down the marquee.
   */
  #teardownMarquee() {
    if (!this.#isActive) return;

    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;

    this.clonedContent?.remove();

    this.#removeRepeatedItems(this.refs.marqueeItems.length - 1);

    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerUp);

    this.#isActive = false;
  }

  /**
   * Desktop: start slowing down on hover.
   */
  #handlePointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#slowDown();
  };

  /**
   * Desktop: speed back up when mouse leaves.
   */
  #handlePointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#speedUp();
  };

  /**
   * Mobile: pause immediately while finger is held down.
   */
  #handlePointerDown = (event) => {
  if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;

  this.#slowDown.cancel();

  const animation = this.#getMarqueeAnimation();

  if (!animation) return;

  this.#animation?.cancel();
  this.#animation = null;

  // Pause exactly at the current position.
  animation.pause();
};

  /**
   * Mobile: resume when finger is released.
   */
  #handlePointerUp = (event) => {
  if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;

  const animation = this.#getMarqueeAnimation();

  if (!animation) return;

  // Continue from exactly where it was paused.
  animation.play();
};

  /**
   * Gets the actual marquee animation.
   */
  #getMarqueeAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    return animations[0] ?? null;
  }

  /**
   * Desktop smooth slowdown.
   */
  #slowDown = debounce(() => {
    if (!this.#isActive || this.#animation) return;

    const animation = this.#getMarqueeAnimation();

    if (!animation) return;

    const currentRate = animation.playbackRate;

    if (currentRate <= 0) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: currentRate,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  /**
   * Desktop smooth speed-up.
   */
  #speedUp = () => {
    if (!this.#isActive) return;

    this.#slowDown.cancel();

    const animation = this.#getMarqueeAnimation();

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? animation.playbackRate ?? 0;

    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  };


  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const { marqueeItems } = this.refs;

    const marqueeWidth = this.offsetWidth;

    const marqueeRepeatedItemWidth =
      marqueeItems[0]?.offsetWidth ?? 1;

    const count =
      marqueeRepeatedItemWidth === 0
        ? 1
        : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);

    return Math.sqrt(count) * speedFactor;
  }

  /**
   * Resize handler.
   *
   * Important:
   * On iOS the browser toolbar can change viewport height while scrolling.
   * That may trigger resize events without the actual layout width changing.
   *
   * We ignore those events so the marquee is not reset to frame 0.
   */
  #handleResize = debounce(() => {
    const currentWidth = window.innerWidth;

    // Ignore height-only / tiny viewport changes.
    if (Math.abs(currentWidth - this.#lastWidth) < 5) {
      return;
    }

    this.#lastWidth = currentWidth;

    const shouldRun = this.#shouldRunMarquee;

    if (shouldRun && !this.#isActive) {
      this.#setupMarquee();
      return;
    }

    if (!shouldRun && this.#isActive) {
      this.#teardownMarquee();
      return;
    }

    if (!shouldRun || !this.#isActive) return;

    const { marqueeItems } = this.refs;

    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = marqueeItems.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(
        newNumberOfCopies - currentNumberOfCopies
      );
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(
        currentNumberOfCopies - newNumberOfCopies
      );
    }

    this.#duplicateContent();
    this.#setSpeed();

    // Only restart because the actual viewport WIDTH changed.
    this.#restartAnimation();
  }, 250);

  /**
   * Restart marquee animation after a real width change.
   */
  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  /**
   * Duplicate the entire marquee content so the loop is seamless.
   */
  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (
      this.refs.content.cloneNode(true)
    );

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * Add repeated items until there is enough content.
   */
  #addRepeatedItems(
    numberOfCopies = this.#calculateNumberOfCopies()
  ) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);

      content.appendChild(clone);
    }
  }

  /**
   * Remove unnecessary repeated items.
   */
  #removeRepeatedItems(
    numberOfCopies = this.#calculateNumberOfCopies()
  ) {
    const { content } = this.refs;

    const children = Array.from(content.children);

    // Always retain at least one original item.
    const itemsToRemove = Math.min(
      numberOfCopies,
      children.length - 1
    );

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }

  /**
   * Calculate how many copies are required.
   */
  #calculateNumberOfCopies() {
    const { marqueeItems } = this.refs;

    const marqueeWidth = this.offsetWidth;

    const marqueeRepeatedItemWidth =
      marqueeItems[0]?.offsetWidth ?? 1;

    return marqueeRepeatedItemWidth === 0
      ? 1
      : Math.ceil(
          marqueeWidth / marqueeRepeatedItemWidth
        );
  }
}

/**
 * Animate a numeric value smoothly.
 *
 * @param {Object} params
 * @param {number} params.from
 * @param {number} params.to
 * @param {number} params.duration
 * @param {function(number): void} params.onUpdate
 * @param {function(number): number} [params.easing]
 * @param {function(): void} [params.onComplete]
 */
function animateValue({
  from,
  to,
  duration,
  onUpdate,
  easing = (t) => t * t * (3 - 2 * t),
  onComplete,
}) {
  const startTime = performance.now();

  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);

    currentValue =
      from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },

    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define(
    'marquee-component',
    MarqueeComponent
  );
}